import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  FormControlLabel,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import ContentPasteGoIcon from '@mui/icons-material/ContentPasteGo';
import ListIcon from '@mui/icons-material/List';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ReportActions } from '../../../../Services/_actions/Report/report.action.js';
import dayjs from 'dayjs';

import SearchableDropdown from '../../../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import CommonTimePicker from '../../../Shared/DateTimePicker/TimePicker.tsx';
import { useSnackBar } from '../../../FlowBuilder/Common/SnackBar/index.tsx';

// ---------- Types ----------
type ScheduleType = {
  type: 'daily' | 'weekly' | 'monthly' | 'custom_days';
  display_name: string;
  description: string;
  required_fields: string[];
  optional_fields: string[];
};

type DeliveryMethodMeta = {
  value: 'email' | 'whatsapp' | 'both';
  display_name: string;
  description: string;
  required_fields: string[];
  optional_fields: string[];
};

type ScheduleOptionsResponseData = {
  schedule_types: ScheduleType[];
  common_timezones: string[];
  days_of_week: { value: string; display: string }[];
  time_options: string[]; // HH:mm
  default_timezone: string;
  default_time: string;   // HH:mm
  email_formats: string[];
  attachment_formats: string[];
  delivery_methods: DeliveryMethodMeta[];
  whatsapp_template_examples: any[];
  template_variables: { variable: string; description: string }[];
  validation_rules: Record<string, string>;
};

type TriggerAPI = {
  id: string;
  name: string;
  schedule_option_key?: string; // older responses
  schedule_type?: string;       // weekly/daily/etc.
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  [k: string]: any;
};

type FormState = {
  name: string;
  is_active: boolean;

  // schedule (flat)
  schedule_type: 'daily' | 'weekly' | 'monthly' | 'custom_days' | '';
  schedule_time?: string;       // "HH:mm"
  timezone?: string;            // "Asia/Kolkata"
  day_of_week?: string;         // for weekly
  day_of_month?: number;        // for monthly
  interval_days?: number;       // for custom_days

  // delivery (single-select in UI, array in payload)
  delivery_methods: Array<'email' | 'whatsapp' | 'both'> | [];
  stakeholder_emails?: string[];
  email_subject?: string;
  email_format?: string;         // "html" | "text"
  attachment_formats?: string[]; // ["html","excel","pdf"]

  // whatsapp (if needed)
  whatsapp_phone_numbers?: string[];
  whatsapp_template_id?: string;
  whatsapp_template_params?: Record<string, any>;
  whatsapp_instance_id?: string;
};

const defaultForm = (meta?: ScheduleOptionsResponseData): FormState => ({
  name: '',
  is_active: true,
  schedule_type: '',
  schedule_time: meta?.default_time ?? '05:00',
  timezone: meta?.default_timezone ?? 'Asia/Kolkata',
  delivery_methods: ['email'],
  stakeholder_emails: [],
  email_subject: '',
  email_format: meta?.email_formats?.[0] ?? 'html',
  attachment_formats: ['html'],
});

const TriggerManagerPage: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const location = useLocation() as {
    state?: { templateName?: string; leads_form_id?: string; leads_form_name?: string; editTriggerId?: string };
  };
  const templateName = location?.state?.templateName || 'Template';
  const editTriggerIdFromState = location?.state?.editTriggerId;

  const actionsRef = useRef(ReportActions());
  const reportActions = actionsRef.current;
  const { showSnackBar } = useSnackBar();

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Data
  const [scheduleMeta, setScheduleMeta] = useState<ScheduleOptionsResponseData | null>(null);
  const [triggers, setTriggers] = useState<TriggerAPI[]>([]);

  // Edit state
  const [selectedTriggerId, setSelectedTriggerId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(defaultForm());
  const isEditing = !!selectedTriggerId;

  // Guards to avoid dependency arrays
  const initializedRef = useRef<boolean>(false);
  const prevTemplateIdRef = useRef<string | undefined>(undefined);
  const appliedEditRef = useRef<boolean>(false);

  // Load helper + triggers (guarded)
  // const loadAll = async (tid?: string) => {
  //   if (!tid) return;
  //   setLoading(true);
  //   try {
  //     const [optRes, trigRes] = await Promise.all([
  //       reportActions.getScheduleOptions(),
  //       reportActions.getTemplateTriggers(tid),
  //     ]);

  //     // normalize schedule options (accept either data.data or data root)
  //     let meta: ScheduleOptionsResponseData | null = null;
  //     if (optRes?.data?.data?.schedule_types) meta = optRes.data.data;
  //     else if (optRes?.data?.schedule_types) meta = optRes.data;
  //     else if (optRes?.schedule_types) meta = optRes;

  //     const rawTrigs = Array.isArray(trigRes?.data) ? trigRes.data : trigRes;
  //     const normalizedTrigs: TriggerAPI[] = Array.isArray(rawTrigs) ? rawTrigs : [];

  //     setScheduleMeta(meta || null);
  //     setTriggers(normalizedTrigs);
  //     setForm(defaultForm(meta || undefined));

  //     // If launched with "editTriggerId" in state, pre-select once
  //     if (editTriggerIdFromState && !appliedEditRef.current) {
  //       const found = normalizedTrigs.find((t: any) => (t?.id ?? t?._id) === editTriggerIdFromState);
  //       if (found) {
  //         appliedEditRef.current = true;
  //         // handleEdit(found);
  //       }
  //     }
  //   } catch (e: any) {
  //     showSnackBar('Failed to load schedule options or triggers', 'error');
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const loadAll = async (tid?: string) => {
  if (!tid) return;
  setLoading(true);
  try {
    const [optRes, trigRes] = await Promise.all([
      reportActions.getScheduleOptions(),
      reportActions.getTemplateTriggers(tid),
    ]);

    // Normalize schedule meta
    let meta: ScheduleOptionsResponseData | null = null;
    if (optRes?.data?.data?.schedule_types) meta = optRes.data.data;
    else if (optRes?.data?.schedule_types) meta = optRes.data;
    else if (optRes?.schedule_types) meta = optRes;

    const rawTrigs = Array.isArray(trigRes?.data?.triggers) ? trigRes.data.triggers : [];
    setScheduleMeta(meta || null);
    setTriggers(rawTrigs);

    // Default form
    setForm(defaultForm(meta || undefined));

    // Pre-fill if editing
   // Pre-fill if editing
if (editTriggerIdFromState && !appliedEditRef.current) {
  const triggerToEdit = rawTrigs.find(t => t.id === editTriggerIdFromState);
  if (triggerToEdit) {
    appliedEditRef.current = true;
    handleEdit(triggerToEdit, meta);
  }
}

  } catch (e: any) {
    showSnackBar('Failed to load schedule options or triggers', 'error');
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    const currentId = templateId;
    if (!initializedRef.current) {
      initializedRef.current = true;
      prevTemplateIdRef.current = currentId;
      loadAll(currentId);
      return;
    }
    // When route param changes
    if (prevTemplateIdRef.current !== currentId) {
      prevTemplateIdRef.current = currentId;
      appliedEditRef.current = false;
      loadAll(currentId);
    }
  });

  // Dropdown options built from helper
  const scheduleTypeOptions = useMemo(
    () =>
      (scheduleMeta?.schedule_types ?? []).map((s) => ({
        label: `${s.display_name} (${s.type})`,
        value: s.type,
        raw: s,
      })),
    [scheduleMeta]
  );
  const timezoneOptions = useMemo(
    () => (scheduleMeta?.common_timezones ?? []).map((tz) => ({ label: tz, value: tz })),
    [scheduleMeta]
  );
  const dowOptions = useMemo(
    () => (scheduleMeta?.days_of_week ?? []).map((d) => ({ label: d.display, value: d.value })),
    [scheduleMeta]
  );
  const deliveryMethodOptions = useMemo(
    () => (scheduleMeta?.delivery_methods ?? []).map((d) => ({ label: d.display_name, value: d.value, raw: d })),
    [scheduleMeta]
  );
  const emailFormatOptions = useMemo(
    () => (scheduleMeta?.email_formats ?? []).map((f) => ({ label: f.toUpperCase(), value: f })),
    [scheduleMeta]
  );
  const attachmentFormatOptions = useMemo(
    () => (scheduleMeta?.attachment_formats ?? []).map((f) => ({ label: f.toUpperCase(), value: f })),
    [scheduleMeta]
  );

  // Helpers
  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleTimeChange = (val: string | null) => {
    // CommonTimePicker returns "hh:mm A" or null; convert to "HH:mm" for payload
    if (!val) return setField('schedule_time', undefined);
    const parsed = dayjs(val, ['h A','ha','hA','h a','h:mm A','h:mma','hh:mm A','hh:mmA'], true);
    setField('schedule_time', parsed.isValid() ? parsed.format('HH:mm') : val);
  };

  const resetForm = () => {
    setSelectedTriggerId(null);
    setForm(defaultForm(scheduleMeta || undefined));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const handleEdit = (t: TriggerAPI, meta?: ScheduleOptionsResponseData) => {
  setSelectedTriggerId(t.id);

  // Parse schedule_cron into HH:mm and type if needed
  const [minute, hour] = t.schedule_cron?.split(' ') || [];
  const scheduleTime = hour && minute ? `${hour.padStart(2,'0')}:${minute.padStart(2,'0')}` : meta?.default_time ?? '05:00';

  setForm({
    name: t.trigger_type || '',
    is_active: t.is_active ?? true,
    schedule_type: 'daily', // TODO: derive from t.schedule_description if needed
    schedule_time: scheduleTime,
    timezone: t.schedule_timezone || meta?.default_timezone || 'Asia/Kolkata',
    day_of_week: undefined,
    day_of_month: undefined,
    interval_days: undefined,
    delivery_methods: Array.isArray(t.delivery_methods) ? t.delivery_methods : ['email'],
    stakeholder_emails: Array.isArray(t.stakeholder_emails) ? t.stakeholder_emails : [],
    email_subject: t.email_subject ?? '',
    email_format: t.email_format ?? (meta?.email_formats?.[0] ?? 'html'),
    attachment_formats: Array.isArray(t.attachment_formats) ? t.attachment_formats : ['html'],
    whatsapp_phone_numbers: Array.isArray(t.whatsapp_phone_numbers) ? t.whatsapp_phone_numbers : [],
    whatsapp_template_id: t.whatsapp_template_id,
    whatsapp_template_params: t.whatsapp_template_params || {},
    whatsapp_instance_id: t.whatsapp_instance_id,
  });

  window.scrollTo({ top: 0, behavior: 'smooth' });
};


  // const handleEdit = (t: TriggerAPI) => {
  //   console.log("tttttt",t)
  //   setSelectedTriggerId(t.id || (t as any)?._id);
  //   // normalize existing fields (prefer flat)
  //   setForm({
  //     name: t.name ?? '',
  //     is_active: Boolean(t.is_active ?? true),

  //     schedule_type: (t.schedule_type || t.schedule_option_key || '') as FormState['schedule_type'],
  //     schedule_time: t.schedule_time ?? scheduleMeta?.default_time ?? '05:00',
  //     timezone: t.timezone ?? scheduleMeta?.default_timezone ?? 'Asia/Kolkata',
  //     day_of_week: t.day_of_week,
  //     day_of_month: t.day_of_month,
  //     interval_days: t.interval_days,

  //     delivery_methods: Array.isArray(t.delivery_methods) ? t.delivery_methods : (t.delivery_methods ? [t.delivery_methods] : ['email']),
  //     stakeholder_emails: Array.isArray(t.stakeholder_emails) ? t.stakeholder_emails : (t.stakeholder_emails ? [t.stakeholder_emails] : []),
  //     email_subject: t.email_subject ?? '',
  //     email_format: t.email_format ?? (scheduleMeta?.email_formats?.[0] || 'html'),
  //     attachment_formats: Array.isArray(t.attachment_formats)
  //       ? t.attachment_formats
  //       : (t.attachment_formats ? String(t.attachment_formats).split(',').map((s: string) => s.trim()) : ['html']),

  //     whatsapp_phone_numbers: Array.isArray(t.whatsapp_phone_numbers)
  //       ? t.whatsapp_phone_numbers
  //       : (t.whatsapp_phone_numbers ? [t.whatsapp_phone_numbers] : []),
  //     whatsapp_template_id: t.whatsapp_template_id,
  //     whatsapp_template_params: t.whatsapp_template_params || {},
  //     whatsapp_instance_id: t.whatsapp_instance_id,
  //   });
  //   window.scrollTo({ top: 0, behavior: 'smooth' });
  // };

  // ---------- Validation helpers ----------
  const isValidEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  const isValidIntlPhone = (p: string) => /^\+[\d\s\-()]{6,}$/.test(p); // simple intl pattern

  const validateBeforeSave = (): string[] => {
    const errors: string[] = [];

    if (!form.name?.trim()) errors.push('Trigger Name is required.');
    if (!form.schedule_type) errors.push('Schedule Type is required.');

    // Schedule field requirements based on helper
    const selectedType = scheduleMeta?.schedule_types.find((s) => s.type === form.schedule_type);
    const requiredSchedule = new Set(selectedType?.required_fields ?? []);
    if (requiredSchedule.has('schedule_time') && !form.schedule_time) {
      errors.push('Schedule time is required for selected schedule type.');
    }
    if (requiredSchedule.has('day_of_week') && !form.day_of_week) {
      errors.push('Day of week is required for weekly schedule.');
    }
    if (requiredSchedule.has('day_of_month') && !form.day_of_month) {
      errors.push('Day of month is required for monthly schedule.');
    }
    if (requiredSchedule.has('interval_days') && !form.interval_days) {
      errors.push('Interval (days) is required for custom interval schedule.');
    }

    // Delivery method (single-select UI)
    const method: 'email' | 'whatsapp' | 'both' | undefined =
      Array.isArray(form.delivery_methods) && form.delivery_methods.length
        ? (form.delivery_methods[0] as any)
        : undefined;

    if (!method) errors.push('Delivery method is required.');

    const methodMeta = scheduleMeta?.delivery_methods.find((d) => d.value === method);
    const reqByMethod = new Set(methodMeta?.required_fields ?? []);

    // Email validations (if method uses email or both)
    const usesEmail = method === 'email' || method === 'both';
    if (usesEmail && reqByMethod.has('stakeholder_emails')) {
      if (!form.stakeholder_emails?.length) {
        errors.push('At least one stakeholder email is required.');
      } else {
        const bad = (form.stakeholder_emails || []).filter((em) => !isValidEmail(em));
        if (bad.length) errors.push(`Invalid email(s): ${bad.join(', ')}`);
      }
    }

    // WhatsApp validations (if method uses whatsapp or both)
    const usesWhatsapp = method === 'whatsapp' || method === 'both';
    if (usesWhatsapp) {
      if (reqByMethod.has('whatsapp_phone_numbers')) {
        if (!form.whatsapp_phone_numbers?.length) {
          errors.push('At least one WhatsApp phone number is required.');
        } else {
          const bad = (form.whatsapp_phone_numbers || []).filter((ph) => !isValidIntlPhone(ph));
          if (bad.length) errors.push(`Invalid WhatsApp number(s): ${bad.join(', ')}`);
        }
      }
      if (reqByMethod.has('whatsapp_template_id') && !form.whatsapp_template_id) {
        errors.push('WhatsApp template ID is required.');
      }
    }

    return errors;
  };

  const save = async () => {
    if (!templateId) return;

    const errors = validateBeforeSave();
    if (errors.length) {
      showSnackBar(errors.join('\n'), 'warning', 7000);
      return;
    }

    // Build flat payload
    const methodSingle: 'email' | 'whatsapp' | 'both' =
      (Array.isArray(form.delivery_methods) && form.delivery_methods.length
        ? form.delivery_methods[0]
        : 'email') as any;

    const payload: any = {
      name: form.name,
      schedule_type: form.schedule_type,
      schedule_time: form.schedule_time, // "HH:mm"
      timezone: form.timezone,
      is_active: form.is_active,

      // delivery — API expects array
      delivery_methods: [methodSingle],
      stakeholder_emails: form.stakeholder_emails ?? [],
      email_subject: form.email_subject,
      email_format: form.email_format,
      attachment_formats: form.attachment_formats ?? [],
    };

    // Add conditional schedule fields
    if (form.schedule_type === 'weekly') payload.day_of_week = form.day_of_week;
    if (form.schedule_type === 'monthly') payload.day_of_month = form.day_of_month;
    if (form.schedule_type === 'custom_days') payload.interval_days = form.interval_days;

    // WhatsApp fields only if selected
    if (methodSingle === 'whatsapp' || methodSingle === 'both') {
      payload.whatsapp_phone_numbers = form.whatsapp_phone_numbers ?? [];
      payload.whatsapp_template_id = form.whatsapp_template_id;
      payload.whatsapp_template_params = form.whatsapp_template_params || {};
      payload.whatsapp_instance_id = form.whatsapp_instance_id;
    }

    setSaving(true);
    try {
      let res;
      if (editTriggerIdFromState && selectedTriggerId) {
        res = await reportActions.updateTemplateTrigger(templateId, selectedTriggerId, payload);
        navigate(`/r/records/templates/${templateId}/triggers/list`, { state: { templateName } });
      } else {
        res = await reportActions.createTemplateTrigger(templateId, payload);
                navigate(`/r/records/templates/${templateId}/triggers/list`, { state: { templateName } });

      }
      const ok = res && (res.status === 200 || res.status === 201 || res?.data);
      if (!ok) {
        const msg = res?.data?.message || 'Failed to save trigger';
        showSnackBar(msg, 'error');
        return;
      }

      showSnackBar(editTriggerIdFromState ? 'Trigger updated successfully' : 'Trigger created successfully', 'success');

      // refresh list to keep state in sync (even though we’re on the editor page)
      const trigRes = await reportActions.getTemplateTriggers(templateId);
      // const rawTrigs = Array.isArray(trigRes?.data) ? trigRes.data : trigRes;
      const rawTrigs = Array.isArray(trigRes?.data?.triggers)
  ? trigRes.data.triggers
  : Array.isArray(trigRes?.triggers)
  ? trigRes.triggers
  : [];

      setTriggers(Array.isArray(rawTrigs) ? rawTrigs : []);

      // reset for new entry
      resetForm();
    } catch (e: any) {
      showSnackBar('Error while saving trigger', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 240 }}>
        <CircularProgress size={22} />
        <Typography variant="body2" sx={{ mt: 1 }}>Loading…</Typography>
      </Stack>
    );
  }

  // ------------------- UI -------------------
  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate(-1)} color="primary" size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Manage Triggers — {templateName}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="outlined"
          size="small"
          startIcon={<ListIcon />}
          onClick={() => navigate(`/r/records/templates/${templateId}/triggers/list`, { state: { templateName } })}
        >
          Go to Trigger List
        </Button>
      </Stack>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {editTriggerIdFromState ? 'Edit Trigger' : 'Create Trigger'}
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {/* Form: force at least 3 per row on md+ via md={4} */}
          <Grid container spacing={1.5}>
            {/* Name */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Trigger Name"
                fullWidth
                size="small"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
              />
            </Grid>

            {/* Active */}
            <Grid item xs={12} sm={6} md={4} display="flex" alignItems="center">
              <FormControlLabel
                control={
                  <Switch
                    checked={form.is_active}
                    onChange={(e) => setField('is_active', e.target.checked)}
                    size="small"
                  />
                }
                label={form.is_active ? 'Active' : 'Inactive'}
              />
            </Grid>

            {/* Schedule Type */}
            <Grid item xs={12} sm={6} md={4}>
              <SearchableDropdown
                options={scheduleTypeOptions}
                labelKey="label"
                valueKey="value"
                label="Schedule Type"
                value={scheduleTypeOptions.find(o => o.value === form.schedule_type) || null}
                onChange={(opt: any) => {
                  const type = opt?.value ?? '';
                  setField('schedule_type', type);
                  // reset conditional fields
                  setField('day_of_week', undefined as any);
                  setField('day_of_month', undefined as any);
                  setField('interval_days', undefined as any);
                }}
                size="small"
              />
            </Grid>

            {/* Time */}
            {(() => {
              const selected = scheduleMeta?.schedule_types.find((s) => s.type === form.schedule_type);
              const required = new Set(selected?.required_fields ?? []);
              if (!required.has('schedule_time')) return null;
              return (
                <Grid item xs={12} sm={6} md={4}>
                  <CommonTimePicker
                    label="Time"
                    value={
                      form.schedule_time
                        ? dayjs(form.schedule_time, 'HH:mm').format('hh:mm A')
                        : scheduleMeta?.default_time
                          ? dayjs(scheduleMeta.default_time, 'HH:mm').format('hh:mm A')
                          : null
                    }
                    onChange={handleTimeChange}
                  />
                </Grid>
              );
            })()}

            {/* Timezone */}
            <Grid item xs={12} sm={6} md={4}>
              <SearchableDropdown
                options={timezoneOptions}
                labelKey="label"
                valueKey="value"
                label="Timezone"
                value={timezoneOptions.find(o => o.value === form.timezone) || null}
                onChange={(opt: any) => setField('timezone', opt?.value || undefined)}
                size="small"
              />
            </Grid>

            {/* Weekly: Day of Week */}
            {(() => {
              const selected = scheduleMeta?.schedule_types.find((s) => s.type === form.schedule_type);
              const required = new Set(selected?.required_fields ?? []);
              if (!required.has('day_of_week')) return null;
              return (
                <Grid item xs={12} sm={6} md={4}>
                  <SearchableDropdown
                    options={dowOptions}
                    labelKey="label"
                    valueKey="value"
                    label="Day of Week"
                    value={dowOptions.find(o => o.value === form.day_of_week) || null}
                    onChange={(opt: any) => setField('day_of_week', opt?.value || undefined)}
                    size="small"
                  />
                </Grid>
              );
            })()}

            {/* Monthly: Day of Month */}
            {(() => {
              const selected = scheduleMeta?.schedule_types.find((s) => s.type === form.schedule_type);
              const required = new Set(selected?.required_fields ?? []);
              if (!required.has('day_of_month')) return null;
              return (
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Day of Month (1-31)"
                    type="number"
                    size="small"
                    value={form.day_of_month ?? ''}
                    onChange={(e) => setField('day_of_month', Number(e.target.value))}
                    inputProps={{ min: 1, max: 31 }}
                    fullWidth
                  />
                </Grid>
              );
            })()}

            {/* Custom Interval: Interval Days */}
            {(() => {
              const selected = scheduleMeta?.schedule_types.find((s) => s.type === form.schedule_type);
              const required = new Set(selected?.required_fields ?? []);
              if (!required.has('interval_days')) return null;
              return (
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Interval (days)"
                    type="number"
                    size="small"
                    value={form.interval_days ?? ''}
                    onChange={(e) => setField('interval_days', Number(e.target.value))}
                    inputProps={{ min: 1 }}
                    fullWidth
                  />
                </Grid>
              );
            })()}

            {/* Delivery Method (single select) */}
            <Grid item xs={12} sm={6} md={4}>
              <SearchableDropdown
                options={deliveryMethodOptions}
                labelKey="label"
                valueKey="value"
                label="Delivery Method"
                value={(Array.isArray(form.delivery_methods) && form.delivery_methods.length)
                  ? deliveryMethodOptions.find(o => o.value === form.delivery_methods[0]) || null
                  : null}
                onChange={(opt: any) => {
                  const single = opt?.value ? [opt.value] : [];
                  setField('delivery_methods', single as any);
                }}
                size="small"
              />
            </Grid>

            {/* Email fields */}
            {(() => {
              const method = (Array.isArray(form.delivery_methods) && form.delivery_methods[0]) || '';
              const emailOn = method === 'email' || method === 'both';
              if (!emailOn) return null;
              return (
                <>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Stakeholder Emails (comma separated)"
                      size="small"
                      value={(form.stakeholder_emails ?? []).join(', ')}
                      onChange={(e) =>
                        setField(
                          'stakeholder_emails',
                          e.target.value.split(',').map((s) => s.trim()).filter(Boolean)
                        )
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="Email Subject"
                      size="small"
                      value={form.email_subject ?? ''}
                      onChange={(e) => setField('email_subject', e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <SearchableDropdown
                      options={emailFormatOptions}
                      labelKey="label"
                      valueKey="value"
                      label="Email Format"
                      value={emailFormatOptions.find(o => o.value === form.email_format) || null}
                      onChange={(opt: any) => setField('email_format', opt?.value || 'html')}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <SearchableDropdown
                      options={attachmentFormatOptions}
                      labelKey="label"
                      valueKey="value"
                      label="Attachment Formats"
                      value={(form.attachment_formats ?? [])
                        .map(f => attachmentFormatOptions.find(o => o.value === f))
                        .filter(Boolean)}
                      onChange={(val: any[] | null) => {
                        const arr = Array.isArray(val) ? val.map((o: any) => o.value) : [];
                        setField('attachment_formats', arr);
                      }}
                      multiSelect
                      size="small"
                    />
                  </Grid>
                </>
              );
            })()}

            {/* WhatsApp fields */}
            {(() => {
              const method = (Array.isArray(form.delivery_methods) && form.delivery_methods[0]) || '';
              const waOn = method === 'whatsapp' || method === 'both';
              if (!waOn) return null;
              return (
                <>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="WhatsApp Phone Numbers (comma separated, intl format)"
                      size="small"
                      value={(form.whatsapp_phone_numbers ?? []).join(', ')}
                      onChange={(e) =>
                        setField(
                          'whatsapp_phone_numbers',
                          e.target.value.split(',').map((s) => s.trim()).filter(Boolean)
                        )
                      }
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="WhatsApp Template ID"
                      size="small"
                      value={form.whatsapp_template_id ?? ''}
                      onChange={(e) => setField('whatsapp_template_id', e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="WhatsApp Template Params (JSON)"
                      size="small"
                      value={JSON.stringify(form.whatsapp_template_params ?? {}, null, 0)}
                      onChange={(e) => {
                        try {
                          const v = e.target.value?.trim();
                          setField('whatsapp_template_params', v ? JSON.parse(v) : {});
                        } catch {
                          // Ignore parse errors in UI; rule hints below
                        }
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      label="WhatsApp Instance ID"
                      size="small"
                      value={form.whatsapp_instance_id ?? ''}
                      onChange={(e) => setField('whatsapp_instance_id', e.target.value)}
                      fullWidth
                    />
                  </Grid>
                </>
              );
            })()}

            {/* Template variables — suggestions */}
            {scheduleMeta?.template_variables?.length ? (
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}
                >
                  <ContentPasteGoIcon fontSize="small" /> Template Variables (click to insert into Email Subject)
                </Typography>
                <Stack direction="row" gap={1} flexWrap="wrap">
                  {scheduleMeta.template_variables.map((tv) => (
                    <Chip
                      key={tv.variable}
                      label={tv.variable}
                      variant="outlined"
                      onClick={() => {
                        const subj = form.email_subject ?? '';
                        const next = subj.includes(tv.variable)
                          ? subj
                          : `${subj}${subj ? ' ' : ''}${tv.variable}`;
                        setField('email_subject', next);
                        showSnackBar(`Inserted ${tv.variable} into Email Subject`, 'success', 3000);
                      }}
                      title={tv.description}
                    />
                  ))}
                </Stack>
                <Paper variant="outlined" sx={{ p: 1, mt: 1, bgcolor: 'background.default' }}>
                  <Stack spacing={0.5}>
                    {scheduleMeta.template_variables.map((tv) => (
                      <Typography key={tv.variable} variant="caption" color="text.secondary">
                        <b>{tv.variable}</b> — {tv.description}
                      </Typography>
                    ))}
                  </Stack>
                </Paper>
              </Grid>
            ) : null}

            {/* Validation Rules — hints */}
            {scheduleMeta?.validation_rules ? (
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}
                >
                  <LightbulbOutlinedIcon fontSize="small" /> Validation Rules (hints)
                </Typography>
                <Paper variant="outlined" sx={{ p: 1 }}>
                  {Object.entries(scheduleMeta.validation_rules).map(([k, v]) => (
                    <Typography key={k} variant="caption" color="text.secondary" display="block">
                      <b>{k}:</b> {v}
                    </Typography>
                  ))}
                </Paper>
              </Grid>
            ) : null}
          </Grid>

          {/* Actions */}
          <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button variant="contained" onClick={save} disabled={saving}>
              {saving ? <CircularProgress size={16} /> : editTriggerIdFromState ? 'Update Trigger' : 'Create Trigger'}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TriggerManagerPage;
        